import { z } from 'zod'

import {
  tagSchema,
  createTagRequestSchema,
  tagCategorySchema,
  tagCategoryRequestSchema,
  type Tag,
  type CreateTagRequest,
  type TagCategory,
  type TagCategoryRequest,
  type TagTargetType,
  type TagAssignmentMode,
} from '@/types/api/tag'

import { apiClient } from './client'

export async function getTags(
  recordGroupId: string,
  targetType: TagTargetType
): Promise<Tag[]> {
  const response = await apiClient.get(`/tags/record-group/${recordGroupId}`, {
    params: { targetType },
  })
  return z.array(tagSchema).parse(response.data)
}

export async function createTag(
  recordGroupId: string,
  body: CreateTagRequest
): Promise<Tag> {
  const validated = createTagRequestSchema.parse(body)
  const response = await apiClient.post(`/tags/record-group/${recordGroupId}`, validated)
  return tagSchema.parse(response.data)
}

export async function getTag(tagId: string, options?: { suppressErrorToast?: boolean }): Promise<Tag> {
  const response = await apiClient.get(`/tags/${tagId}`, options)
  return tagSchema.parse(response.data)
}

export async function setTagAssignmentMode(tagId: string, assignmentMode: TagAssignmentMode): Promise<Tag> {
  const response = await apiClient.put(`/tags/${tagId}/assignment-mode`, { assignmentMode })
  return tagSchema.parse(response.data)
}

export async function archiveTag(tagId: string): Promise<void> {
  await apiClient.delete(`/tags/${tagId}`)
}

/** Move a tag into a category, or out of its category with null. */
export async function moveTagToCategory(tagId: string, categoryId: string | null): Promise<Tag> {
  const response = await apiClient.put(`/tags/${tagId}/category`, { categoryId })
  return tagSchema.parse(response.data)
}

// ── Tag categories ─────────────────────────────────────────────────────────

export async function getTagCategories(recordGroupId: string, targetType: TagTargetType): Promise<TagCategory[]> {
  const response = await apiClient.get(`/tags/record-group/${recordGroupId}/categories`, {
    params: { targetType },
  })
  return z.array(tagCategorySchema).parse(response.data)
}

export async function createTagCategory(recordGroupId: string, body: TagCategoryRequest): Promise<TagCategory> {
  const validated = tagCategoryRequestSchema.parse(body)
  const response = await apiClient.post(`/tags/record-group/${recordGroupId}/categories`, validated)
  return tagCategorySchema.parse(response.data)
}

export async function updateTagCategory(categoryId: string, body: TagCategoryRequest): Promise<TagCategory> {
  const validated = tagCategoryRequestSchema.parse(body)
  const response = await apiClient.put(`/tags/categories/${categoryId}`, validated)
  return tagCategorySchema.parse(response.data)
}

/** Deletes the category; its tags stay, uncategorized. */
export async function deleteTagCategory(categoryId: string): Promise<void> {
  await apiClient.delete(`/tags/categories/${categoryId}`)
}

// ── Record-tag endpoints (record module, not tag module) ───────────────────

const recordTagSchema = z.object({
  tagId: z.string().uuid(),
  label: z.string(),
  colorHex: z.string().nullable(),
})

export type RecordTag = z.infer<typeof recordTagSchema>

export async function getRecordTags(recordId: string): Promise<RecordTag[]> {
  const response = await apiClient.get(`/records/${recordId}/tags`)
  return z.array(recordTagSchema).parse(response.data)
}

export async function assignRecordTag(recordId: string, tagId: string): Promise<void> {
  await apiClient.post(`/records/${recordId}/tags`, { tagId })
}

export async function unassignRecordTag(recordId: string, tagId: string): Promise<void> {
  await apiClient.delete(`/records/${recordId}/tags/${tagId}`)
}
